import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../config/firebase";
import {
  syncAuthLocalMirror,
  clearAuthLocalMirror,
} from "../utils/authLocalStorage";
import { getFirestoreTrustedUntilMillis } from "../constants/adminSession";

const AuthContext = createContext(null);

/**
 * Single auth listener for the app. Wrap the tree in <AuthProvider>.
 * Source of truth: Firebase Auth + Firestore users/{email}; localStorage is a cleared/synced mirror only.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        clearAuthLocalMirror();
        setUser(null);
        setLoading(false);
        return;
      }

      const email = firebaseUser.email || "";

      try {
        let userDoc = email
          ? await getDoc(doc(db, "users", email))
          : await getDoc(doc(db, "users", firebaseUser.uid));
        if (email && !userDoc.exists()) {
          userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
        }

        if (userDoc.exists()) {
          const data = userDoc.data();
          const userData = {
            id: email || firebaseUser.uid,
            email: data.email || email || firebaseUser.email || "",
            permissions: data.permissions || "parentsUser",
            employeeId: data.employeeId || "",
            adminSessionTrustedUntilMs:
              typeof data.adminSessionTrustedUntilMs === "number"
                ? data.adminSessionTrustedUntilMs
                : getFirestoreTrustedUntilMillis(data),
          };
          syncAuthLocalMirror(userData);
          setUser(userData);
        } else {
          const userData = {
            id: firebaseUser.uid,
            email: firebaseUser.email || "",
            permissions: "childUser",
            employeeId: "",
          };
          syncAuthLocalMirror(userData);
          setUser(userData);
        }
      } catch (err) {
        console.error("[useAuth] Failed to fetch user permissions:", err);
        const cachedEmail = localStorage.getItem("userEmail");
        const cachedPerms = localStorage.getItem("userPermissions");
        if (
          cachedPerms &&
          cachedEmail &&
          firebaseUser.email &&
          cachedEmail === firebaseUser.email
        ) {
          setUser({
            id: localStorage.getItem("currentUserId") || firebaseUser.uid,
            email: cachedEmail,
            permissions: cachedPerms,
            employeeId: localStorage.getItem("userEmployeeId") || "",
            adminSessionTrustedUntilMs: null,
          });
        } else {
          const userData = {
            id: firebaseUser.uid,
            email: firebaseUser.email || "",
            permissions: "childUser",
            employeeId: "",
          };
          syncAuthLocalMirror(userData);
          setUser(userData);
        }
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = useCallback((userData) => {
    syncAuthLocalMirror(userData);
    setUser(userData);
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
    clearAuthLocalMirror();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      isAdmin: user?.permissions === "admin",
      login,
      logout,
    }),
    [user, loading, login, logout],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx == null) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
